import React from 'react';
import './EmptyState.css';

/**
 * Baustein — Empty-State.
 *
 * Standard-Patterns:
 *   - variant='standard'  Liste leer, optional Primary Action
 *   - variant='filter'    Filter ergibt 0 Treffer, keine Action
 *   - variant='error'     Fehlerzustand, optional Retry-Action (Icon AlertTriangle empfohlen).
 *                         `onRetry` macht das obere Icon selbst klickbar (Reload) — es bleibt
 *                         dabei ein graues Icon analog den anderen Varianten, KEIN rotes
 *                         Warndreieck und kein separater Button.
 *
 * Sonderform:
 *   - variant='onboarding'  Sammlung-Erstaufruf-Artikel (Escape-Hatch via children).
 *                           NICHT im Master-Showcase dokumentiert, weil
 *                           Sammlung-spezifisch und kein wiederverwendbares Pattern.
 *
 * Props:
 *   icon        — lucide-react Icon-Component (z.B. Users)
 *   title       — kurze Beschreibung
 *   description — optional, längerer Satz Kontext unter dem Titel
 *   action      — optional, ReactNode (Primary-Button), nur bei variant='standard' oder 'error'
 *   variant     — 'standard' | 'filter' | 'error' | 'onboarding' (default 'standard')
 *   size        — 'default' | 'compact' (default 'default'); 'compact' = kompakter
 *                 Listen-Leerzustand (kleineres Icon + Padding), für Inline-Einsatz
 *                 in einer Liste, wenn weitere Sektionen folgen.
 *   onRetry     — optional fn; macht das obere Icon klickbar (z.B. Reload bei variant='error')
 *   children    — nur bei variant='onboarding' verwendet (eigenes Markup)
 */
export default function EmptyState({ icon: Icon, title, description, action, variant = 'standard', size = 'default', onRetry, children }) {
  if (variant === 'onboarding') {
    return <div className="empty-state empty-state--onboarding">{children}</div>;
  }

  const showAction = (variant === 'standard' || variant === 'error') && action;
  const iconSize = size === 'compact' ? 28 : 48;

  return (
    <div className={`empty-state empty-state--${variant}${size === 'compact' ? ' empty-state--compact' : ''}`}>
      {Icon && (
        onRetry ? (
          <button type="button" className="empty-state__icon-button" onClick={onRetry} aria-label="Erneut versuchen">
            <Icon size={iconSize} className="empty-state__icon" aria-hidden />
          </button>
        ) : (
          <Icon size={iconSize} className="empty-state__icon" aria-hidden />
        )
      )}
      <p className="empty-state__title">{title}</p>
      {description && <p className="empty-state__description">{description}</p>}
      {showAction && <div className="empty-state__action">{action}</div>}
    </div>
  );
}
